import { useCallback, useEffect, useId, useMemo, useRef, useState, useSyncExternalStore, type DragEvent, type JSX, type KeyboardEvent } from "react";
import { SMART_LIST_GROUPS, type SmartListMenuGroup, type SmartListsResponse, type SmartListSummary } from "@gioia365/contracts";
import { normalizeSearchText } from "@gioia365/domain/search-text";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Icon } from "../design/components/Icon";
import { useToast } from "../design/components/Toast";
import { messageForError } from "../i18n/errors";
import { formatCount } from "../i18n/format";
import { useLocale, useT } from "../i18n/locale";
import { useOptionalTab } from "../session/access";
import type { TabContext } from "../session/tab-context";
import { prefetchSmartList, smartListsQuery, useSaveSmartListOrder } from "./smart-list-queries";
import "./smart-lists.css";

// Il sottomenu della voce «Smart List» nella rail (piano M3 Task 8, mock-up `renderShell` con `LIST_GROUPS`): le liste
// che chi guarda vede, divise nei gruppi «Tue», «Condivise con te», «Del team», «Dei colleghi» (i gruppi vuoti non si
// vedono), ognuna con il numero dei suoi Lead e la lista aperta segnata; da sei liste in su «Cerca una lista». In fondo
// non c'è «Crea una Smart List»: le liste nascono dall'elenco Lead (product owner, 29 settembre 2026). Il guscio lo
// mostra sotto la voce (`RailItem.flyout`); `onNavigate` richiude il pannello del telefono quando si sceglie una lista.
// Il puntatore o il focus su una lista la legge in anticipo, come le voci della rail (decisione P28). Il menu porta al
// massimo cento liste: oltre, una riga lo dice, e «Cerca una lista» cerca soltanto fra quelle (decisione P29).
// Il guscio tiene il sottomenu nella pagina anche a rail chiusa (`RailItem.flyout`): da chiuso mostra ciò che la cache ha
// già (la lettura in anticipo del guscio) e non legge nulla, nemmeno al ritorno sulla finestra; all'apertura rilegge il
// menu se non è più fresco, come quando nasceva aprendo la rail. Con la rail aperta e fissa (da 1280 px, riscontro del
// product owner del 3 ottobre 2026) il sottomenu è sempre aperto, quindi le liste sono sempre in vista.
// Accanto a ogni lista, dopo il nome e prima del numero, il pallino con le sue «novità» (`newCount`, questione aperta 182),
// che la pagina della lista azzera aprendola; con la rail a icone, un pallino sull'icona «Smart List» dice che una lista
// ne ha (`SmartListRailNews`).
// Le fissate e l'ordine (SL-11, richiesta del product owner del 23 ottobre 2026) sono di chi guarda: in cima il gruppo
// «Fissate», poi i gruppi di sempre, ognuno nell'ordine che la persona ha scelto. Accanto a ogni lista, al passaggio del
// puntatore o con il focus, «Sposta su», «Sposta giù» e la puntina («Fissa in alto» / «Togli dalle fissate»); sulle fissate la
// puntina si vede sempre. Le liste si trascinano anche (drag and drop del browser, senza librerie), soltanto dentro il loro
// gruppo e non col dito; con la tastiera, oltre ai pulsanti, Alt+freccia su e giù sul nome. Mentre «Cerca una lista» filtra si fissa ma non
// si sposta: l'ordine di una parte del gruppo non direbbe dove vanno le altre. Ogni mossa si vede subito e si salva da sola;
// se il server la rifiuta il menu torna com'era e un avviso lo dice.

/** Da quante liste compare «Cerca una lista», come nel mock-up. */
const SEARCH_FROM = 6;

export function SmartListRail({ open, onNavigate }: { open: boolean; onNavigate(): void }): JSX.Element | null {
  const tab = useOptionalTab();
  return tab ? <RailLists tab={tab} open={open} onNavigate={onNavigate} /> : null;
}

/** Le mosse del menu: il nuovo ordine di un gruppo, tutto intero, dall'alto (`PUT /api/smart-lists/order`). */
type Reorder = (group: SmartListMenuGroup, ids: string[]) => void;
type Dragging = { group: SmartListMenuGroup; id: string };
type DropAt = { id: string; after: boolean };

/** L'elenco `ids` con `id` spostato al posto `to` (dopo averlo tolto). */
function moved(ids: readonly string[], id: string, to: number): string[] {
  const rest = ids.filter((other) => other !== id);
  rest.splice(Math.max(0, Math.min(to, rest.length)), 0, id);
  return rest;
}

function RailLists({ tab, open, onNavigate }: { tab: TabContext; open: boolean; onNavigate(): void }): JSX.Element | null {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const menu = useQuery({ ...smartListsQuery(tab), enabled: open });
  const { save } = useSaveSmartListOrder(tab, () => toast.show(t("smartLists.orderFailed"), "danger"));
  const touch = useCoarsePointer();
  const searchId = useId();
  const [find, setFind] = useState("");
  const [dragging, setDragging] = useState<Dragging | null>(null);
  const [dropAt, setDropAt] = useState<DropAt | null>(null);
  // La lista la cui puntina riprende il focus quando è arrivata nell'altro gruppo (fissata o tolta): la mossa ottimistica
  // arriva un attimo dopo il clic, e prima di lei la riga è ancora quella di prima.
  const [refocusPin, setRefocusPin] = useState<{ id: string; pinned: boolean } | null>(null);
  // Richiuso, il sottomenu dimentica la ricerca, come quando lasciava la pagina chiudendosi.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) setFind("");
  }
  const key = normalizeSearchText(find);
  const lists = menu.data?.lists;
  const searchable = (lists?.length ?? 0) >= SEARCH_FROM;
  const searching = searchable && key !== "";
  const shown = useMemo(() => {
    if (!lists) return [];
    return searching ? lists.filter((list) => normalizeSearchText(list.name).includes(key)) : lists;
  }, [lists, searching, key]);
  const reorder = useCallback<Reorder>((group, ids) => save({ group, ids }), [save]);
  const togglePin = useCallback(
    (list: SmartListSummary) => {
      const pinned = (lists ?? []).filter((item) => item.pinned).map((item) => item.id);
      setRefocusPin({ id: list.id, pinned: !list.pinned });
      reorder("pinned", list.pinned ? pinned.filter((id) => id !== list.id) : [...pinned, list.id]);
    },
    [lists, reorder],
  );

  if (menu.isError && !lists) {
    return (
      <div className="g-smart-rail">
        <p className="g-smart-rail__note">{messageForError(t, menu.error)}</p>
        <button type="button" className="g-smart-rail__retry" onClick={() => void menu.refetch()}>
          {t("common.retry")}
        </button>
      </div>
    );
  }
  if (!lists) {
    return (
      <div className="g-smart-rail">
        <p className="g-smart-rail__note">{t("common.loading")}</p>
      </div>
    );
  }
  // Senza liste il sottomenu resta vuoto: la pagina della voce spiega come nasce una lista.
  if (lists.length === 0) return null;

  const groups: Array<{ group: SmartListMenuGroup; lists: SmartListSummary[] }> = [
    { group: "pinned", lists: shown.filter((list) => list.pinned) },
    ...SMART_LIST_GROUPS.map((group) => ({ group, lists: shown.filter((list) => !list.pinned && list.group === group) })),
  ];
  const drag = {
    dragging,
    dropAt,
    start: (group: SmartListMenuGroup, id: string) => setDragging({ group, id }),
    over: (at: DropAt | null) => setDropAt((current) => (current?.id === at?.id && current?.after === at?.after ? current : at)),
    leave: (id: string) => setDropAt((current) => (current?.id === id ? null : current)),
    end: () => {
      setDragging(null);
      setDropAt(null);
    },
  };

  return (
    <div className="g-smart-rail">
      {searchable ? (
        <>
          <label className="g-visually-hidden" htmlFor={searchId}>{t("smartLists.findList")}</label>
          <input
            id={searchId}
            className="g-field__input g-smart-rail__search"
            type="search"
            autoComplete="off"
            placeholder={t("smartLists.findList")}
            value={find}
            onChange={(event) => setFind(event.target.value)}
          />
        </>
      ) : null}
      {menu.data?.truncated ? <p className="g-smart-rail__note">{t("smartLists.truncated", { count: formatCount(locale, lists.length) })}</p> : null}
      <ul className="g-smart-rail__groups" aria-label={t("smartLists.railLabel")}>
        {groups.map(({ group, lists: inGroup }) =>
          inGroup.length > 0 ? (
            <RailGroup
              key={group}
              tab={tab}
              group={group}
              label={t(`smartLists.groups.${group}`)}
              lists={inGroup}
              canMove={!searching}
              canDrag={!searching && !touch}
              reorder={reorder}
              togglePin={togglePin}
              drag={drag}
              refocusPin={refocusPin}
              onRefocused={() => setRefocusPin(null)}
              onNavigate={onNavigate}
            />
          ) : null,
        )}
      </ul>
    </div>
  );
}

type DragState = {
  dragging: Dragging | null;
  dropAt: DropAt | null;
  start(group: SmartListMenuGroup, id: string): void;
  over(at: DropAt | null): void;
  leave(id: string): void;
  end(): void;
};

type GroupProps = {
  tab: TabContext;
  group: SmartListMenuGroup;
  label: string;
  lists: readonly SmartListSummary[];
  canMove: boolean;
  canDrag: boolean;
  reorder: Reorder;
  togglePin(list: SmartListSummary): void;
  drag: DragState;
  refocusPin: { id: string; pinned: boolean } | null;
  onRefocused(): void;
  onNavigate(): void;
};

function RailGroup({ tab, group, label, lists, canMove, canDrag, reorder, togglePin, drag, refocusPin, onRefocused, onNavigate }: GroupProps) {
  const labelId = useId();
  const ids = lists.map((list) => list.id);
  const movable = canMove && lists.length > 1;
  const draggable = canDrag && movable;
  return (
    <li className="g-smart-rail__group">
      <span id={labelId} className="g-smart-rail__group-label">{label}</span>
      <ul aria-labelledby={labelId}>
        {lists.map((list, index) => (
          <RailLink
            key={list.id}
            tab={tab}
            list={list}
            first={index === 0}
            last={index === lists.length - 1}
            movable={movable}
            draggable={draggable}
            onMove={(step) => reorder(group, moved(ids, list.id, index + step))}
            onTogglePin={() => togglePin(list)}
            dragging={drag.dragging?.id === list.id}
            dropAt={drag.dropAt?.id === list.id && drag.dragging?.group === group ? (drag.dropAt.after ? "after" : "before") : null}
            onDragStart={() => drag.start(group, list.id)}
            acceptsDrop={drag.dragging !== null && drag.dragging.group === group && drag.dragging.id !== list.id}
            onDragOver={(after) => drag.over({ id: list.id, after })}
            onDragLeave={() => drag.leave(list.id)}
            onDrop={(after) => {
              const from = drag.dragging;
              drag.end();
              if (!from || from.group !== group || from.id === list.id) return;
              const rest = ids.filter((id) => id !== from.id);
              const target = rest.indexOf(list.id) + (after ? 1 : 0);
              const next = moved(ids, from.id, target);
              if (next.some((id, at) => id !== ids[at])) reorder(group, next);
            }}
            onDragEnd={drag.end}
            refocusPin={refocusPin?.id === list.id && refocusPin.pinned === list.pinned}
            onRefocused={onRefocused}
            onNavigate={onNavigate}
          />
        ))}
      </ul>
    </li>
  );
}

type LinkProps = {
  tab: TabContext;
  list: SmartListSummary;
  first: boolean;
  last: boolean;
  movable: boolean;
  draggable: boolean;
  onMove(step: -1 | 1): void;
  onTogglePin(): void;
  dragging: boolean;
  dropAt: "before" | "after" | null;
  acceptsDrop: boolean;
  onDragStart(): void;
  onDragOver(after: boolean): void;
  onDragLeave(): void;
  onDrop(after: boolean): void;
  onDragEnd(): void;
  refocusPin: boolean;
  onRefocused(): void;
  onNavigate(): void;
};

/** Un puntatore grosso (il dito): niente trascinamento, che col tocco litiga con la pressione lunga sul link; restano i pulsanti. */
function useCoarsePointer(): boolean {
  const subscribe = useCallback((notify: () => void) => {
    const query = typeof window.matchMedia === "function" ? window.matchMedia("(pointer: coarse)") : null;
    query?.addEventListener("change", notify);
    return () => query?.removeEventListener("change", notify);
  }, []);
  return useSyncExternalStore(subscribe, () => typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches);
}

/** Se il puntatore sta nella metà bassa della riga: la lista trascinata va dopo, altrimenti prima. */
const inLowerHalf = (event: DragEvent<HTMLElement>): boolean => {
  const box = event.currentTarget.getBoundingClientRect();
  return box.height > 0 && event.clientY > box.top + box.height / 2;
};

/**
 * Una lista: il nome è il nome del link, il numero si vede accanto e arriva come descrizione («12 Lead»). Un conteggio
 * che il server non è riuscito a fare si vede come «—», mai come zero, e lo dice per esteso (decisione P7). Le novità,
 * se ce ne sono, in un pallino fra il nome e il numero, e nella descrizione dopo il numero («12 Lead 3 nuovi»). Accanto,
 * fuori dal link, «Sposta su», «Sposta giù» e la puntina (SL-11): pulsanti veri, con il nome della lista nel loro nome. In
 * cima e in fondo al gruppo il pulsante che non può muovere resta lì, `aria-disabled`, così il focus non si perde.
 */
function RailLink(props: LinkProps) {
  const { tab, list, first, last, movable, draggable, onMove, onTogglePin, dragging, dropAt, acceptsDrop, refocusPin, onRefocused, onNavigate } = props;
  const t = useT();
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const countId = useId();
  const newsId = useId();
  const pinRef = useRef<HTMLButtonElement>(null);
  // I pulsanti entrano nell'ordine del Tab soltanto quando il focus è nella riga: con cento liste, il Tab non passa da trecento
  // pulsanti nascosti. Dal nome, Tab arriva ai pulsanti della sua riga; Alt+freccia sposta senza lasciarlo.
  const [focused, setFocused] = useState(false);
  const toolTab = focused ? 0 : -1;
  useEffect(() => {
    if (!refocusPin) return;
    pinRef.current?.focus();
    onRefocused();
  }, [refocusPin, onRefocused]);
  const prefetch = () => void prefetchSmartList(queryClient, tab, list.id);
  let shown: string;
  let spoken: string;
  if (list.count === null) {
    shown = t("smartLists.countMissing");
    spoken = t("smartLists.countUnavailable");
  } else {
    shown = formatCount(locale, list.count);
    spoken = list.count === 1 ? t("smartLists.countOne") : t("smartLists.countMany", { count: shown });
  }
  const news = list.count !== null && list.newCount !== null && list.newCount > 0 ? list.newCount : 0;
  const newsShown = formatCount(locale, news);
  const move = (step: -1 | 1) => {
    if ((step === -1 && first) || (step === 1 && last)) return;
    onMove(step);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) => {
    if (!movable || !event.altKey || (event.key !== "ArrowUp" && event.key !== "ArrowDown")) return;
    event.preventDefault();
    move(event.key === "ArrowUp" ? -1 : 1);
  };
  return (
    <li
      className="g-smart-rail__item"
      data-pinned={list.pinned ? "true" : undefined}
      data-dragging={dragging ? "true" : undefined}
      data-drop={dropAt ?? undefined}
      draggable={draggable}
      onFocus={() => setFocused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
      }}
      onDragStart={(event) => {
        if (!draggable) return;
        event.dataTransfer?.setData("text/plain", list.id);
        if (event.dataTransfer) event.dataTransfer.effectAllowed = "move";
        props.onDragStart();
      }}
      onDragOver={(event) => {
        if (!acceptsDrop) return;
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
        props.onDragOver(inLowerHalf(event));
      }}
      onDragLeave={(event) => {
        if (!(event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget))) props.onDragLeave();
      }}
      onDrop={(event) => {
        if (!acceptsDrop) return;
        event.preventDefault();
        props.onDrop(inLowerHalf(event));
      }}
      onDragEnd={props.onDragEnd}
    >
      <Link
        to="/smart-list/$smartListId"
        params={{ smartListId: list.id }}
        className="g-smart-rail__link"
        aria-describedby={news > 0 ? `${countId} ${newsId}` : countId}
        draggable={false}
        onClick={onNavigate}
        onPointerEnter={prefetch}
        onFocus={prefetch}
        onKeyDown={onKeyDown}
      >
        {list.pinned ? <Icon name="pushpin" className="g-smart-rail__pinned" /> : null}
        <span className="g-smart-rail__name">{list.name}</span>
        {news > 0 ? (
          <span className="g-smart-rail__new" aria-hidden="true">
            {newsShown}
          </span>
        ) : null}
        <span className="g-smart-rail__count" aria-hidden="true" title={list.count === null ? spoken : undefined}>
          {shown}
        </span>
      </Link>
      <span className="g-smart-rail__tools">
        {movable ? (
          <>
            <button
              type="button"
              className="g-smart-rail__tool"
              tabIndex={toolTab}
              aria-label={t("smartLists.moveUpList", { name: list.name })}
              title={t("smartLists.moveUp")}
              aria-disabled={first ? "true" : undefined}
              onClick={() => move(-1)}
            >
              <Icon name="up" />
            </button>
            <button
              type="button"
              className="g-smart-rail__tool"
              tabIndex={toolTab}
              aria-label={t("smartLists.moveDownList", { name: list.name })}
              title={t("smartLists.moveDown")}
              aria-disabled={last ? "true" : undefined}
              onClick={() => move(1)}
            >
              <Icon name="down" />
            </button>
          </>
        ) : null}
        <button
          ref={pinRef}
          type="button"
          className="g-smart-rail__tool"
          tabIndex={toolTab}
          data-active={list.pinned ? "true" : undefined}
          aria-label={t(list.pinned ? "smartLists.unpinList" : "smartLists.pinList", { name: list.name })}
          title={t(list.pinned ? "smartLists.unpin" : "smartLists.pin")}
          onClick={onTogglePin}
        >
          <Icon name="pushpin" />
        </button>
      </span>
      <span id={countId} className="g-visually-hidden">{spoken}</span>
      {news > 0 ? (
        <span id={newsId} className="g-visually-hidden">
          {news === 1 ? t("smartLists.newOne") : t("smartLists.newMany", { count: newsShown })}
        </span>
      ) : null}
    </li>
  );
}

/**
 * Il menu che la cache ha già, seguito senza osservarlo come una query: nessuna lettura, e nessuna voce della cache
 * creata o tenuta in vita (quando la scheda perde l'azienda, la cache della scheda si svuota davvero).
 */
function useCachedMenu(tab: TabContext): SmartListsResponse | undefined {
  const queryClient = useQueryClient();
  const queryKey = smartListsQuery(tab).queryKey;
  const subscribe = useCallback((notify: () => void) => queryClient.getQueryCache().subscribe(notify), [queryClient]);
  return useSyncExternalStore(subscribe, () => queryClient.getQueryData(queryKey));
}

/**
 * Il pallino sull'icona «Smart List» quando la rail è a icone (`RailItem.indicator`): c'è se una lista del menu ha
 * novità, e lo dice a parole nell'elemento `id`, la descrizione della voce («Ci sono Lead nuovi nelle Smart List»),
 * mai nel suo nome. Legge soltanto ciò che il menu in cache sa già (la lettura in anticipo del guscio, il sottomenu):
 * nessuna richiesta in più; nella Vista Commerciale il menu è quello della vista, con le novità della persona osservata.
 */
export function SmartListRailNews({ id }: { id: string }): JSX.Element | null {
  const tab = useOptionalTab();
  return tab ? <RailNews tab={tab} id={id} /> : null;
}

function RailNews({ tab, id }: { tab: TabContext; id: string }): JSX.Element | null {
  const t = useT();
  const menu = useCachedMenu(tab);
  const any = menu?.lists.some((list) => list.count !== null && (list.newCount ?? 0) > 0) ?? false;
  if (!any) return null;
  // Il pallino si vede soltanto; le parole stanno in un elemento nascosto che la voce usa come descrizione.
  return (
    <>
      <span className="g-nav__dot" aria-hidden="true" />
      <span id={id} hidden>
        {t("smartLists.railNews")}
      </span>
    </>
  );
}
